import { err, invalidState, notFound, ok, validate, validationError, type Result } from '../errors/errors';
import type { Deps } from '../interfaces/stores';
import { BallotSchema, type Ballot, type SpeakerNote } from '../schemas/entities';
import type { SideIndex } from '../schemas/format';
import { setRoundStatus } from './rounds';

type D<K extends keyof Deps> = Pick<Deps, K>;

export const emptyBallot = (roundId: string, now: number): Ballot => ({
  roundId,
  assessments: {},
  weighing: { side0: [], side1: [], winner: null, note: '' },
  winnerSide: null,
  committedAt: null,
  rfd: '',
  points: {},
  teamComments: { A: '', B: '' },
  speakerComments: {},
  speakerNotes: {},
  roundNotes: '',
  submittedAt: null,
  updatedAt: now,
});

export const loadBallot = (d: D<'ballots' | 'clock'>) => async (roundId: string): Promise<Ballot> =>
  (await d.ballots.get(roundId)) ?? emptyBallot(roundId, d.clock.now());

/** Save a partial draft. Decision fields are locked once committed; writing fields are always editable. */
export const saveBallot = (d: D<'ballots' | 'rounds' | 'clock' | 'tx'>) => (roundId: string, patch: Partial<Ballot>): Promise<Result<Ballot>> =>
  // In a transaction so a speaker note saved at the same moment isn't overwritten by this read-modify-write.
  d.tx.run(() => saveBallotNow(d)(roundId, patch));

const saveBallotNow = (d: D<'ballots' | 'rounds' | 'clock'>) => async (roundId: string, patch: Partial<Ballot>): Promise<Result<Ballot>> => {
  const round = await d.rounds.get(roundId);
  if (!round) return err(notFound('round', roundId));
  const cur = await loadBallot(d)(roundId);
  if (cur.committedAt !== null && (patch.winnerSide !== undefined || patch.assessments !== undefined || patch.weighing !== undefined)) {
    return err(invalidState('ballot.committed', 'Your decision is locked. Reopen it first if you need to change it.'));
  }
  const next = { ...cur, ...patch, roundId, updatedAt: d.clock.now() };
  const v = validate(BallotSchema, next, { rfd: 'Reason for decision' });
  if (!v.ok) return v;

  // Points must match the format's scale and step; collect every problem.
  const { min, max, step } = round.format.points;
  const issues = Object.entries(v.value.points).flatMap(([k, p]) => {
    if (p === null) return [];
    if (p < min || p > max) return [{ path: `points.${k}`, code: 'out_of_range', message: `Points must be between ${min} and ${max}.` }];
    const steps = (p - min) / step;
    if (Math.abs(steps - Math.round(steps)) > 1e-6) return [{ path: `points.${k}`, code: 'step', message: `Points go in steps of ${step}.` }];
    return [];
  });
  if (issues.length) return err(validationError(issues));

  await d.ballots.put(v.value);
  return v;
};

/** Decide first, then write: the winner is locked before any RFD tooling (including future AI) is used. */
/** Save one speaker's private note (text and quick tags). Works whether or not the decision is committed. */
export const saveSpeakerNote = (d: D<'ballots' | 'clock' | 'tx'>) => (roundId: string, key: string, note: SpeakerNote): Promise<Result<Ballot>> =>
  d.tx.run(async () => {
    const cur = await loadBallot(d)(roundId);
    const v = validate(BallotSchema, { ...cur, speakerNotes: { ...(cur.speakerNotes ?? {}), [key]: note }, updatedAt: d.clock.now() }, { 'speakerNotes.*.text': 'Speaker note' });
    if (v.ok) await d.ballots.put(v.value);
    return v;
  });

export const commitDecision = (d: D<'ballots' | 'rounds' | 'clock' | 'tx'>) => async (roundId: string, winnerSide: SideIndex): Promise<Result<Ballot>> => {
  const cur = await loadBallot(d)(roundId);
  if (cur.committedAt !== null) return err(invalidState('ballot.committed', 'This decision is already committed.'));
  const saved = await saveBallot(d)(roundId, { winnerSide });
  if (!saved.ok) return saved;
  const committed = { ...saved.value, committedAt: d.clock.now() };
  await d.ballots.put(committed);
  await setRoundStatus(d)(roundId, 'committed');
  return ok(committed);
};

export const reopenDecision = (d: D<'ballots' | 'rounds' | 'clock'>) => async (roundId: string): Promise<Result<Ballot>> => {
  const cur = await d.ballots.get(roundId);
  if (!cur) return err(notFound('ballot', roundId));
  if (cur.submittedAt !== null) return err(invalidState('ballot.submitted', 'Already submitted to Tabroom. Undo "submitted" first.'));
  const next = { ...cur, committedAt: null, updatedAt: d.clock.now() };
  await d.ballots.put(next);
  await setRoundStatus(d)(roundId, 'deciding');
  return ok(next);
};

/** Mark as submitted in Tabroom; schedules debater-name purge per settings. */
export const markSubmitted = (d: D<'ballots' | 'rounds' | 'names' | 'settings' | 'clock'>) => async (roundId: string, submitted: boolean): Promise<Result<Ballot>> => {
  const cur = await d.ballots.get(roundId);
  if (!cur || cur.committedAt === null) return err(invalidState('ballot.notCommitted', 'Commit your decision before marking it submitted.'));
  const now = d.clock.now();
  const next = { ...cur, submittedAt: submitted ? now : null, updatedAt: now };
  await d.ballots.put(next);
  await setRoundStatus(d)(roundId, submitted ? 'submitted' : 'committed');
  const names = await d.names.get(roundId);
  if (names) {
    const { purgeNamesAfterHours } = await d.settings.get();
    await d.names.put({ ...names, purgeAfter: submitted ? now + purgeNamesAfterHours * 3_600_000 : null });
  }
  return ok(next);
};
